// Inline backend URL to avoid circular import issues
const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8787';

export interface UploadProgress {
    phase: 'downloading' | 'uploading';
    percent: number;
    bytesTransferred: number;
    totalBytes: number;
    speed: number;
}

/**
 * Get Feishu file download URL
 * If directUrl is provided (from SDK), use it directly
 * Otherwise call backend API to get the URL
 */
export async function getFeishuFileUrl(
    fileToken: string,
    metadata: {
        appToken: string;
        tableId: string;
        recordId: string;
        fieldId: string;
    },
    directUrl?: string  // URL from SDK if available
): Promise<string> {
    // If we have a direct URL from the SDK, use it
    if (directUrl) {
        console.log('[Direct Upload] Using SDK-provided URL');
        return directUrl;
    }

    // Fallback to backend API
    console.log('[Direct Upload] Falling back to backend API for URL');
    const params = new URLSearchParams(metadata);
    const response = await fetch(
        `${backendUrl}/api/feishu/file-url/${fileToken}?${params}`
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get file URL');
    }

    const data = await response.json();
    return data.downloadUrl;
}

/**
 * Download file from Feishu with progress tracking
 */
export async function downloadFromFeishu(
    downloadUrl: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', downloadUrl, true);
        xhr.responseType = 'blob';

        const startTime = Date.now();

        xhr.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                const now = Date.now();
                const duration = (now - startTime) / 1000;
                const speed = duration > 0 ? event.loaded / duration : 0;

                onProgress({
                    phase: 'downloading',
                    percent: Math.round((event.loaded / event.total) * 100),
                    bytesTransferred: event.loaded,
                    totalBytes: event.total,
                    speed
                });
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve(xhr.response);
            } else {
                reject(new Error(`Download failed: ${xhr.statusText}`));
            }
        };

        xhr.onerror = () => reject(new Error('Network error during download'));
        xhr.send();
    });
}

/**
 * Create Google Drive upload session
 */
export async function createDriveUploadSession(
    fileName: string,
    fileSize: number,
    renameFieldValue?: string
): Promise<string> {
    const response = await fetch(`${backendUrl}/api/drive/upload-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName, fileSize, renameFieldValue })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create upload session');
    }

    const data = await response.json();
    return data.uploadUrl;
}

/**
 * Upload file to Google Drive with chunking
 */
export async function uploadToDriveDirect(
    file: Blob,
    uploadUrl: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<string> {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const totalSize = file.size;
    let uploadedBytes = 0;
    const startTime = Date.now();

    for (let start = 0; start < totalSize; start += CHUNK_SIZE) {
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunk = file.slice(start, end);

        const response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Length': chunk.size.toString(),
                'Content-Range': `bytes ${start}-${end - 1}/${totalSize}`
            },
            body: chunk
        });

        uploadedBytes = end;

        if (onProgress) {
            const duration = (Date.now() - startTime) / 1000;
            const speed = duration > 0 ? uploadedBytes / duration : 0;

            onProgress({
                phase: 'uploading',
                percent: Math.round((uploadedBytes / totalSize) * 100),
                bytesTransferred: uploadedBytes,
                totalBytes: totalSize,
                speed
            });
        }

        // Check if upload is complete
        if (response.status === 200 || response.status === 201) {
            const result = await response.json();
            return result.id; // Google Drive file ID
        }

        if (!response.ok && response.status !== 308) {
            throw new Error(`Upload failed: ${response.statusText}`);
        }
    }

    throw new Error('Upload completed but no file ID returned');
}

/**
 * Complete direct upload flow
 */
export async function directUploadFile(
    fileToken: string,
    fileName: string,
    metadata: {
        appToken: string;
        tableId: string;
        recordId: string;
        fieldId: string;
    },
    renameFieldValue?: string,
    onProgress?: (progress: UploadProgress) => void,
    attachmentUrl?: string  // Direct URL from SDK if available
): Promise<string> {
    // 1. Get download URL (use SDK URL if available, otherwise call backend)
    const downloadUrl = await getFeishuFileUrl(fileToken, metadata, attachmentUrl);

    // 2. Download from Feishu
    const blob = await downloadFromFeishu(downloadUrl, onProgress);

    // 3. Create upload session
    const uploadUrl = await createDriveUploadSession(
        fileName,
        blob.size,
        renameFieldValue
    );

    // 4. Upload to Drive
    const fileId = await uploadToDriveDirect(blob, uploadUrl, onProgress);

    return fileId;
}
